import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Initials } from "@/lib/avatar";
import DirectoryCover from "@/components/DirectoryCover";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  MagnifyingGlass, CircleNotch, Phone, EnvelopeSimple,
  Cake, Heart, MapPin, LockKey, UserPlus, PencilSimple, UserMinus, WarningCircle, CaretDown,
} from "@phosphor-icons/react";

const MONTHS = ["", "Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const dm = (d, m) => (d && m ? `${String(d).padStart(2, "0")} ${MONTHS[m]}` : "—");
const LIFECYCLE = ["active", "inactive", "resigned", "deceased"];

const RoleBadge = ({ label }) => (
  <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-[#E8F1FA] text-[#0067C8]">{label}</span>
);
const SourceBadge = ({ source }) => (
  <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${
    source === "club" ? "bg-[#FBF1DA] text-[#7A4B00]" : "bg-black/5 text-muted-foreground"}`}>
    {source === "club" ? "Club-added" : "RI"}
  </span>
);
const StatusBadge = ({ status, membership }) => {
  if (status === "active") return null;
  return (
    <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-[#FBE3E3] text-[#8A1C1C] capitalize">
      {membership || "inactive"}
    </span>
  );
};

const emptyForm = { full_name: "", call_name: "", email: "", mobile: "", classification: "", rotary_id: "", spouse_name: "", address: "" };

export default function Directory() {
  const params = useParams();
  const navigate = useNavigate();
  const [clubs, setClubs] = useState([]);
  const [activeClub, setActiveClub] = useState(null);
  const [data, setData] = useState(null);
  const [manageData, setManageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [showInactive, setShowInactive] = useState(false);
  const [open, setOpen] = useState(false);
  const [profile, setProfile] = useState(null);
  const [profileErr, setProfileErr] = useState(false);
  const [profileLoading, setProfileLoading] = useState(false);
  // maintenance dialogs
  const [form, setForm] = useState(null);        // { mode:'add'|'edit', member_id?, values }
  const [statusFor, setStatusFor] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/directory/clubs");
        setClubs(r.data.clubs);
        if (r.data.clubs.length) {
          const preferred = params.clubId && r.data.clubs.some((c) => c.club_id === params.clubId)
            ? params.clubId : r.data.clubs[0].club_id;
          setActiveClub(preferred);
        }
      } catch (_) {}
    })();
  }, []);

  const reload = async (club) => {
    setLoading(true);
    try {
      const r = await api.get(`/directory/clubs/${club}/members`);
      setData(r.data);
      if (r.data.can_manage) {
        const mr = await api.get(`/clubs/${club}/members/manage`);
        setManageData(mr.data);
      } else {
        setManageData(null);
      }
    } catch (_) { setData(null); setManageData(null); }
    setLoading(false);
  };
  useEffect(() => { if (activeClub) reload(activeClub); }, [activeClub]); // eslint-disable-line

  const canManage = !!data?.can_manage;
  const source = canManage ? manageData : data;

  const grouped = useMemo(() => {
    if (!source) return [];
    let list = source.members;
    if (canManage && !showInactive) list = list.filter((m) => m.status === "active");
    list = list.filter((m) => (m.full_name || "").toLowerCase().includes(q.toLowerCase()));
    const map = {};
    list.forEach((m) => { const l = (m.full_name || "#")[0].toUpperCase(); (map[l] = map[l] || []).push(m); });
    return Object.keys(map).sort().map((l) => ({ letter: l, members: map[l] }));
  }, [source, q, showInactive, canManage]);

  const openProfile = async (member_id) => {
    setOpen(true); setProfile(null); setProfileErr(false); setProfileLoading(true);
    try { setProfile((await api.get(`/directory/members/${member_id}/profile`)).data); }
    catch (_) { setProfileErr(true); }
    setProfileLoading(false);
  };

  const openEdit = async (m) => {
    setForm({ mode: "edit", member_id: m.member_id, loading: true, values: {
      full_name: m.full_name || "", call_name: m.call_name || "", email: m.email || "",
      mobile: m.mobile || "", classification: m.classification || "", rotary_id: m.rotary_id || "",
      spouse_name: "", address: "",
      birthday_day: "", birthday_month: "", anniversary_day: "", anniversary_month: "" } });
    try {
      const p = (await api.get(`/directory/members/${m.member_id}/profile`)).data;
      const pr = p.profile || {};
      setForm((f) => (f && f.member_id === m.member_id) ? { ...f, loading: false, values: { ...f.values,
        spouse_name: p.spouse_name || "", address: p.address || "",
        birthday_day: pr.birthday_day || "", birthday_month: pr.birthday_month || "",
        anniversary_day: pr.anniversary_day || "", anniversary_month: pr.anniversary_month || "" } } : f);
    } catch (_) { setForm((f) => f ? { ...f, loading: false } : f); }
  };

  const saveMember = async () => {
    setBusy(true);
    try {
      if (form.mode === "add") {
        await api.post(`/clubs/${activeClub}/members`, form.values);
        toast.success("Member added");
      } else {
        const v = form.values;
        const num = (x) => (x === "" || x === null || x === undefined ? null : Number(x));
        const payload = {
          full_name: v.full_name, call_name: v.call_name, email: v.email, mobile: v.mobile,
          classification: v.classification, rotary_id: v.rotary_id, spouse_name: v.spouse_name, address: v.address,
          birthday_day: num(v.birthday_day), birthday_month: num(v.birthday_month),
          anniversary_day: num(v.anniversary_day), anniversary_month: num(v.anniversary_month),
        };
        await api.put(`/clubs/${activeClub}/members/${form.member_id}`, payload);
        toast.success("Member updated");
      }
      setForm(null); await reload(activeClub);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save member"); }
    setBusy(false);
  };

  const changeStatus = async (newStatus) => {
    setBusy(true);
    try {
      await api.post(`/clubs/${activeClub}/members/${statusFor.member_id}/status`, { status: newStatus });
      toast.success(`Marked ${newStatus}`);
      setStatusFor(null); await reload(activeClub);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not update status"); }
    setBusy(false);
  };

  return (
    <div className="max-w-5xl" data-testid="directory-page">
      {params.clubId && (
        <button data-testid="back-to-club-dashboard" onClick={() => navigate(`/club/${params.clubId}`)}
          className="mb-4 inline-flex items-center gap-1.5 text-[14px] font-semibold text-primary">
          <span aria-hidden>←</span> Back to club dashboard
        </button>
      )}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="c-title">Directory</h1>
        {canManage && (
          <button data-testid="add-member-button" onClick={() => setForm({ mode: "add", values: { ...emptyForm } })}
            className="inline-flex items-center gap-2 bg-primary text-white px-4 py-2.5 text-[14px] font-semibold rounded-full shadow-sm hover:bg-primary/90 active:scale-95 transition min-h-[44px]">
            <UserPlus size={17} weight="bold" /> Add member
          </button>
        )}
      </div>

      <div className="mt-6 flex flex-wrap gap-2" data-testid="directory-club-tabs">
        {clubs.map((c) => (
          <button key={c.club_id} data-testid={`club-tab-${c.club_id}`} onClick={() => setActiveClub(c.club_id)}
            className={`px-4 py-2 text-[14px] font-semibold rounded-full transition-colors active:scale-95 min-h-[40px] ${
              activeClub === c.club_id ? "bg-primary text-white shadow-sm"
                : "bg-white text-muted-foreground shadow-[0_1px_2px_rgba(84,86,90,0.06)] hover:text-foreground"}`}>
            {c.club_name}
          </button>
        ))}
      </div>

      {canManage && activeClub && <div className="mt-6"><DirectoryCover clubId={activeClub} /></div>}
      {canManage && activeClub && <DirectoryPreview clubId={activeClub} />}

      <div className="mt-6 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px] max-w-sm">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input data-testid="directory-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name…"
            className="w-full pl-9 pr-3 py-2.5 text-sm bg-white rounded-full shadow-[0_1px_2px_rgba(84,86,90,0.06)] focus:ring-2 focus:ring-primary focus:outline-none min-h-[44px]" />
        </div>
        {canManage && (
          <label data-testid="show-inactive-toggle" className="flex items-center gap-2 text-[14px] text-muted-foreground cursor-pointer select-none">
            <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} className="accent-primary w-4 h-4" />
            Show inactive
          </label>
        )}
      </div>

      {loading ? (
        <div className="mt-10 flex items-center gap-2 text-muted-foreground text-sm">
          <CircleNotch size={18} className="animate-spin text-primary" weight="bold" /> loading members…
        </div>
      ) : (
        <div className="mt-6 space-y-8" data-testid="directory-list">
          {grouped.length === 0 && <p className="text-sm text-muted-foreground">No members match your search.</p>}
          {grouped.map(({ letter, members }) => (
            <div key={letter}>
              <div className="flex items-center gap-3 mb-3">
                <span className="font-display font-extrabold text-primary text-lg">{letter}</span>
                <span className="h-px flex-1 bg-black/10 dark:bg-white/10" />
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                {members.map((m) => (
                  <div key={m.member_id} data-testid={`member-row-${m.member_id}`}
                    className={`flex items-center gap-3 bg-white rounded-2xl shadow-[0_1px_2px_rgba(84,86,90,0.06)] p-4 ${m.status && m.status !== "active" ? "opacity-70" : ""}`}>
                    <button onClick={() => openProfile(m.member_id)} className="flex items-center gap-3 min-w-0 flex-1 text-left transition-transform hover:-translate-y-0.5 active:scale-[0.99]">
                      <Initials name={m.full_name} size={44} />
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-sm truncate flex items-center gap-1.5 flex-wrap">
                          {m.full_name}
                          {m.call_name && <span className="text-muted-foreground">· {m.call_name}</span>}
                          {canManage && <SourceBadge source={m.source} />}
                          {canManage && <StatusBadge status={m.status} membership={m.membership_status} />}
                          {m.profile_complete === false && (
                            <span data-testid={`incomplete-${m.member_id}`} title="Details missing"
                              className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#7A4B00]">
                              <span style={{ color: "#F7A81B" }}>▲</span> details missing</span>
                          )}
                        </p>
                        <div className="mt-1 flex flex-wrap gap-1.5 items-center">
                          {m.roles?.length ? m.roles.map((r) => <RoleBadge key={r} label={r} />)
                            : <span className="text-[11px] text-muted-foreground">Member</span>}
                          {m.needs_replacement && (
                            <span data-testid={`needs-replacement-${m.member_id}`} className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#8A1C1C]">
                              <WarningCircle size={13} weight="fill" /> replacement needed
                            </span>
                          )}
                        </div>
                      </div>
                    </button>
                    {canManage && (
                      <div className="flex flex-col gap-1.5 shrink-0">
                        <button data-testid={`edit-member-${m.member_id}`} title="Edit"
                          onClick={() => openEdit(m)}
                          className="p-2 rounded-lg text-primary hover:bg-primary/5 min-h-[36px] min-w-[36px] flex items-center justify-center">
                          <PencilSimple size={16} weight="bold" />
                        </button>
                        <button data-testid={`status-member-${m.member_id}`} title="Membership status"
                          onClick={() => setStatusFor(m)}
                          className="p-2 rounded-lg text-muted-foreground hover:bg-black/5 min-h-[36px] min-w-[36px] flex items-center justify-center">
                          <UserMinus size={16} weight="bold" />
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add / edit member */}
      <Dialog open={!!form} onOpenChange={(o) => !o && setForm(null)}>
        <DialogContent data-testid="member-form-dialog" className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">{form?.mode === "add" ? "Add member" : "Edit member"}</DialogTitle>
          </DialogHeader>
          {form && (
            <div className="space-y-3">
              {[["full_name", "Full name *", "text"], ["call_name", "Call name", "text"],
                ["email", "Email", "email"], ["mobile", "Mobile", "tel"],
                ["classification", "Classification", "text"], ["rotary_id", "Rotary ID (optional)", "text"],
                ["spouse_name", "Spouse / partner name", "text"], ["address", "Mailing address", "text"]].map(([k, label, type]) => (
                <label key={k} className="block">
                  <span className="text-xs font-semibold text-muted-foreground">{label}</span>
                  <input data-testid={`member-field-${k}`} type={type} value={form.values[k]}
                    onChange={(e) => setForm((f) => ({ ...f, values: { ...f.values, [k]: e.target.value } }))}
                    className="mt-1 w-full px-3 py-2.5 text-sm bg-white border border-border rounded-md focus:ring-2 focus:ring-primary focus:outline-none min-h-[44px]" />
                </label>
              ))}
              {form.mode === "edit" && (
                <div className="rounded-xl border border-border/70 bg-black/[0.02] p-3">
                  <p className="mb-2 text-xs font-semibold text-muted-foreground">Directory extras (day &amp; month only — year is never stored)</p>
                  {[["birthday", "Birthday"], ["anniversary", "Anniversary"]].map(([base, label]) => (
                    <div key={base} className="mb-2 flex items-center gap-2 last:mb-0">
                      <span className="w-24 text-xs font-semibold text-foreground">{label}</span>
                      <input data-testid={`member-field-${base}_day`} type="number" min="1" max="31" placeholder="Day"
                        value={form.values[`${base}_day`]}
                        onChange={(e) => setForm((f) => ({ ...f, values: { ...f.values, [`${base}_day`]: e.target.value } }))}
                        className="w-20 px-2 py-2 text-sm bg-white border border-border rounded-md focus:ring-2 focus:ring-primary focus:outline-none min-h-[40px]" />
                      <select data-testid={`member-field-${base}_month`}
                        value={form.values[`${base}_month`]}
                        onChange={(e) => setForm((f) => ({ ...f, values: { ...f.values, [`${base}_month`]: e.target.value } }))}
                        className="flex-1 px-2 py-2 text-sm bg-white border border-border rounded-md focus:ring-2 focus:ring-primary focus:outline-none min-h-[40px]">
                        <option value="">Month</option>
                        {["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"].map((mo, i) => (
                          <option key={mo} value={i + 1}>{mo}</option>
                        ))}
                      </select>
                    </div>
                  ))}
                </div>
              )}
              {form.mode === "add" && (
                <p className="text-[11px] text-muted-foreground">Rotary ID is optional now — you can add it later once RI issues it. New active members are immediately available for board & committee assignments.</p>
              )}
            </div>
          )}
          <DialogFooter>
            <button data-testid="save-member-button" onClick={saveMember} disabled={busy || !form?.values.full_name.trim()}
              className="inline-flex items-center gap-2 bg-primary text-white px-5 py-2.5 text-[14px] font-semibold rounded-full hover:bg-primary/90 active:scale-95 transition disabled:opacity-60 min-h-[44px]">
              {busy ? <CircleNotch size={15} className="animate-spin" weight="bold" /> : <UserPlus size={15} weight="bold" />} Save
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Membership status */}
      <Dialog open={!!statusFor} onOpenChange={(o) => !o && setStatusFor(null)}>
        <DialogContent data-testid="member-status-dialog" className="max-w-sm">
          <DialogHeader><DialogTitle className="font-display tracking-tight">Membership status</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">
            Set the status for <span className="text-foreground font-medium">{statusFor?.full_name}</span>. History and past appointments are always preserved — members are never deleted.
          </p>
          <div className="mt-2 grid grid-cols-2 gap-2">
            {LIFECYCLE.map((s) => (
              <button key={s} data-testid={`set-status-${s}`} onClick={() => changeStatus(s)} disabled={busy || statusFor?.membership_status === s}
                className={`px-3 py-2.5 text-[14px] font-semibold rounded-lg border transition capitalize min-h-[44px] ${
                  s === "active" ? "border-emerald-300 text-emerald-800 hover:bg-emerald-50"
                    : "border-border text-foreground hover:bg-black/5"} disabled:opacity-40`}>
                {s}
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Full profile dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="member-profile-dialog" className="max-w-md">
          <DialogHeader><DialogTitle className="font-display tracking-tight">Member profile</DialogTitle></DialogHeader>
          {profileLoading ? (
            <div className="flex items-center gap-2 text-muted-foreground text-sm py-6">
              <CircleNotch size={18} className="animate-spin text-primary" weight="bold" /> loading…
            </div>
          ) : profileErr ? (
            <div data-testid="profile-restricted" className="flex items-start gap-3 py-4 text-sm text-muted-foreground">
              <LockKey size={20} weight="duotone" className="text-accent shrink-0 mt-0.5" />
              Full profile is restricted. Only the member, their own-club President/Secretary, or a District Admin may view it.
            </div>
          ) : profile ? (
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <Initials name={profile.full_name} size={52} />
                <div>
                  <p className="font-display font-extrabold text-lg leading-tight">{profile.full_name}</p>
                  <p className="text-xs text-muted-foreground">{profile.club_name}</p>
                </div>
              </div>
              <dl className="text-sm divide-y divide-black/5 dark:divide-white/5 border border-black/10 dark:border-white/10">
                <Row icon={Phone} label="Mobile" value={profile.mobile} />
                <Row icon={EnvelopeSimple} label="Email" value={profile.email} />
                <Row label="Classification" value={profile.classification} />
                <Row icon={Heart} label="Spouse" value={profile.spouse_name} />
                <Row icon={Cake} label="Birthday" value={dm(profile.profile?.birthday_day, profile.profile?.birthday_month)} />
                <Row icon={Cake} label="Spouse birthday" value={dm(profile.profile?.spouse_birthday_day, profile.profile?.spouse_birthday_month)} />
                <Row icon={Heart} label="Anniversary" value={dm(profile.profile?.anniversary_day, profile.profile?.anniversary_month)} />
                <Row icon={MapPin} label="Address" value={profile.address} />
              </dl>
              <p className="text-[11px] text-muted-foreground">Dates are stored as day &amp; month only — never the year.</p>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Row = ({ icon: Icon, label, value }) => (
  <div className="flex items-center justify-between gap-4 px-4 py-2.5">
    <span className="flex items-center gap-2 text-xs uppercase tracking-[0.12em] text-muted-foreground">
      {Icon && <Icon size={14} weight="duotone" />} {label}
    </span>
    <span className="text-sm text-right truncate">{value || "—"}</span>
  </div>
);

// On-screen directory preview: Board, Committees and Sponsored clubs, mirroring the PDF.
function DirectoryPreview({ clubId }) {
  const [open, setOpen] = useState(true);
  const [d, setD] = useState(null);
  useEffect(() => {
    let on = true;
    api.get(`/clubs/${clubId}/directory`).then((r) => on && setD(r.data)).catch(() => on && setD({ error: true }));
    return () => { on = false; };
  }, [clubId]);
  if (!d || d.error) return null;
  const board = d.board || [];
  const committees = d.committees || [];
  const sponsored = d.sponsored || [];
  return (
    <div className="mt-6 rounded-2xl bg-white shadow-[0_1px_2px_rgba(84,86,90,0.06)]" data-testid="directory-preview">
      <button data-testid="directory-preview-toggle" onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center justify-between gap-3 px-5 py-4 text-left">
        <div>
          <p className="text-[15px] font-semibold text-foreground">Directory preview</p>
          <p className="text-[13px] text-muted-foreground">Board and committees are pulled from where you built them. The members list is below.</p>
        </div>
        <CaretDown size={18} className={`shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="space-y-6 border-t px-5 py-5" style={{ borderColor: "#EEF0F5" }}>
          <section data-testid="preview-board">
            <p className="mb-2 text-[13px] font-semibold uppercase tracking-wide text-primary">Club Board</p>
            {board.length === 0
              ? <p className="text-[13px] text-muted-foreground">No board positions assigned yet.</p>
              : <div className="grid gap-1.5 sm:grid-cols-2">
                  {board.map((b, i) => (
                    <div key={i} className="flex items-baseline justify-between gap-3 border-b py-1.5" style={{ borderColor: "#F0F1F6" }}>
                      <span className="text-[13px] text-muted-foreground">{b.title}</span>
                      <span className="text-[14px] font-semibold text-foreground text-right">{b.member || "—"}</span>
                    </div>
                  ))}
                </div>}
          </section>
          <section data-testid="preview-committees">
            <p className="mb-2 text-[13px] font-semibold uppercase tracking-wide text-primary">Committees</p>
            {committees.length === 0
              ? <p className="text-[13px] text-muted-foreground">No committees built yet.</p>
              : <div className="space-y-3">
                  {committees.map((c, i) => (
                    <div key={i}>
                      <p className="text-[13.5px] font-semibold text-foreground">{c.name}</p>
                      <div className="mt-1 flex flex-wrap gap-1.5">
                        {c.members.map((m, j) => (
                          <span key={j} className="rounded-full bg-[#F3F3FE] px-2.5 py-1 text-[12px] font-medium text-[#4B4DDB]">
                            {m.member} · {m.role}
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>}
          </section>
          {sponsored.map((s, i) => (
            <section key={i} data-testid={`preview-sponsored-${i}`}>
              <p className="mb-2 text-[13px] font-semibold uppercase tracking-wide text-primary">{s.title}</p>
              <div className="space-y-1.5">
                {s.clubs.map((cl, j) => (
                  <div key={j} className="border-b py-1.5" style={{ borderColor: "#F0F1F6" }}>
                    <p className="text-[13.5px] font-semibold text-foreground">{cl.club_name}</p>
                    <p className="text-[12px] text-muted-foreground">
                      {[cl.president_name && `Pres: ${cl.president_name}`, cl.phone, cl.email].filter(Boolean).join("  ·  ")}
                    </p>
                  </div>
                ))}
              </div>
            </section>
          ))}
        </div>
      )}
    </div>
  );
}
